/**
 * MYTHIVA™ brand system.
 *
 * Hierarchy:
 *   • Product/technology brand : MYTHIVA™           (dominant customer-facing identity)
 *   • Legal company            : Mythos India Studios Private Limited (footers/legal only)
 *   • Module inside MYTHIVA    : LifeVault (household vault), EduSphere (coming), etc.
 */
export const PARENT_URL = "https://mythosindiastudio.com/";
export const PARENT_CONTACT_URL = "https://mythosindiastudio.com/contact";
export const PARENT_NAME = "Mythos India Studios Private Limited";
export const PARENT_CIN = "U62010TS2026PTC221089";
export const MYTHIVA_LOGO_SRC = "/brand/mythiva_wordmark.png";
export const PARENT_LOGO_SRC = "/brand/mythos_color_logo.png";
export const PRODUCT_TAGLINE = "By Mythos India Studios Private Limited";

/**
 * MythivaWordmark — the horizontal MYTHIVA™ logo. Use as the primary brand mark.
 * @param height  pixel height (width is derived, aspect preserved)
 * @param module  optional module label (e.g. "LifeVault") shown as a sub-line
 */
export function MythivaWordmark({ height = 32, module: moduleName, testid, className = "" }) {
    // MYTHIVA horizontal wordmark is 2000×452 (aspect 4.42:1). Fix width to preserve mark integrity.
    const width = Math.round(height * (2000 / 452));
    return (
        <div className={`inline-flex items-center gap-3 ${className}`} data-testid={testid}>
            <img
                src={MYTHIVA_LOGO_SRC}
                alt="MYTHIVA"
                width={width}
                height={height}
                style={{ height, width, minWidth: width, flexShrink: 0 }}
                className="object-contain select-none"
            />
            {moduleName ? (
                <div className="pl-3 border-l border-border/70 leading-tight">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
                        Module
                    </div>
                    <div className="font-semibold tracking-tight" style={{ fontSize: height * 0.55 }}>
                        {moduleName}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

/**
 * BrandLockup — MYTHIVA™ mark + optional module + optional "by MYTHIVA™" line.
 * Backwards-compatible with previous callsites (size prop retained).
 */
export function BrandLockup({
    size = "md",
    parent = false,
    module: moduleName,
    className = "",
    testid,
}) {
    const heightBySize = { sm: 32, md: 44, lg: 64 };
    const h = heightBySize[size] || heightBySize.md;
    return (
        <div data-testid={testid} className={`flex flex-col gap-1.5 ${className}`}>
            <MythivaWordmark height={h} module={moduleName} />
            {parent ? (
                <span
                    role="link"
                    tabIndex={0}
                    data-testid="brand-parent-link"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.open(PARENT_URL, "_blank", "noopener,noreferrer");
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            window.open(PARENT_URL, "_blank", "noopener,noreferrer");
                        }
                    }}
                    className="cursor-pointer text-[9px] uppercase tracking-[0.28em] text-muted-foreground hover:text-foreground font-medium transition-colors whitespace-nowrap"
                >
                    by Mythos India Studios Pvt Ltd ↗
                </span>
            ) : null}
        </div>
    );
}

/**
 * MythosBrand — the parent/legal corporate mark. Used in footers and legal notices.
 * Clicking opens the parent site in a new tab.
 */
export function MythosBrand({ size = "md", className = "", showCIN = false, testid = "mythos-brand" }) {
    const sizes = {
        sm: { mark: 30, name: "text-sm" },
        md: { mark: 40, name: "text-base" },
        lg: { mark: 56, name: "text-xl" },
    };
    const s = sizes[size] || sizes.md;
    return (
        <a
            href={PARENT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={testid}
            className={`inline-flex items-center gap-3 group ${className}`}
            title="Open MYTHIVA™ ↗"
        >
            <img
                src={PARENT_LOGO_SRC}
                alt="MYTHIVA™"
                width={s.mark}
                height={s.mark}
                className="object-contain"
                style={{ width: s.mark, height: s.mark }}
            />
            <div className="leading-tight">
                <div className={`font-semibold tracking-tight ${s.name} group-hover:text-primary transition-colors`}>
                    Mythos India Studios Private Limited
                    <span className="ml-1 text-muted-foreground group-hover:text-primary">↗</span>
                </div>
                <div className="mt-1 text-[9px] uppercase tracking-[0.32em] text-muted-foreground font-medium">
                    Parent company · MYTHIVA™ is a product of MYTHIVA™
                </div>
                {showCIN ? (
                    <div className="mt-1 text-[9px] uppercase tracking-[0.28em] text-muted-foreground/80 font-mono">
                        CIN {PARENT_CIN}
                    </div>
                ) : null}
            </div>
        </a>
    );
}

/** Tiny inline badge — links to parent site. */
export function MythosPlatformBadge({ className = "", testid = "mythos-platform-badge" }) {
    return (
        <a
            href={PARENT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={testid}
            className={`inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-muted-foreground hover:text-foreground transition-colors ${className}`}
        >
            <img
                src={PARENT_LOGO_SRC}
                alt=""
                aria-hidden="true"
                width={16}
                height={16}
                className="object-contain"
                style={{ width: 16, height: 16 }}
            />
            A MYTHIVA™ platform ↗
        </a>
    );
}
